import { useState } from "react";

import {
  toDiscoveryState,
  toErrorMessage,
} from "../../../../components/Input/InstrumentSearch";
import {
  DiscoveredListing,
  useLazyDiscoverListingsQuery,
} from "../../../../services/instruments";
import { InstrumentIdentity, ListingDiscoveryResult } from "./types";

const candidatesOf = (
  state: ReturnType<typeof toDiscoveryState>,
): DiscoveredListing[] => {
  if (state.status === "found") {
    return [state.listing];
  }
  return state.status === "ambiguous" ? state.candidates : [];
};

const isSameIdentity = (a: InstrumentIdentity, b: InstrumentIdentity) =>
  a.symbol === b.symbol && a.exchange === b.exchange;

// A result only counts for the identity it was found for, so editing the symbol or exchange retires it.
export const useListingDiscovery = (
  identity: InstrumentIdentity,
  initialResult: ListingDiscoveryResult | null = null,
) => {
  const [result, setResult] = useState(initialResult);
  const [discoverListings, { isFetching: isFinding }] =
    useLazyDiscoverListingsQuery();

  const find = async (
    target: InstrumentIdentity,
  ): Promise<DiscoveredListing[]> => {
    setResult(null);
    try {
      const candidates = candidatesOf(
        toDiscoveryState(await discoverListings(target).unwrap()),
      );
      setResult({ identity: target, candidates });
      return candidates;
    } catch (err: unknown) {
      setResult({ identity: target, error: toErrorMessage(err) });
      return [];
    }
  };

  const current =
    result && isSameIdentity(result.identity, identity) ? result : null;
  const candidates =
    current && "candidates" in current ? current.candidates : [];
  const hasNoMatch = Boolean(
    current && "candidates" in current && candidates.length === 0,
  );
  const error = current && "error" in current ? current.error : null;

  return { result, candidates, hasNoMatch, error, isFinding, find };
};
